/**
 * Edit the people on a project — the requestor (PM), the role contacts (interior
 * designer / CSM / sales executive), and the ad-hoc "other contacts" who get
 * status-update emails. Supervisor/admin only; available at any status. Reuses
 * the same directory autocomplete + recipients editor as the intake request form,
 * and persists through PATCH /projects/:id (useUpdateProject).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { Project, StatusUpdateRecipient } from '@vari-crew/schemas';
import { useRecipients } from '@/api/recipients';
import { useUpdateProject } from '@/api/projects';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Input';
import { RecipientAutocomplete } from '@/components/ui/RecipientAutocomplete';
import { OtherRecipientsEditor } from '@/components/intake/OtherRecipientsEditor';

export function EditPeopleDialog({
  project,
  open,
  onClose,
}: {
  project: Project;
  open: boolean;
  onClose: () => void;
}) {
  const { data: rec } = useRecipients(open);
  const recipients = rec?.data ?? [];
  const update = useUpdateProject(project.id);

  // Requestor (a platform user → pm_user_id). Picking from the directory sets it;
  // typing without picking is display-only and discarded on save.
  const [pmUserId, setPmUserId] = useState<string | null>(project.pm_user_id);
  const [pmName, setPmName] = useState(project.pm_display_name ?? '');
  const [pmEmail, setPmEmail] = useState(project.pm_email ?? '');
  // Role contacts — name + email (directory pick or custom).
  const [idn, setIdn] = useState(project.interior_designer ?? '');
  const [idEmail, setIdEmail] = useState(project.interior_designer_email ?? '');
  const [csm, setCsm] = useState(project.csm ?? '');
  const [csmEmail, setCsmEmail] = useState(project.csm_email ?? '');
  const [sales, setSales] = useState(project.sales_executive ?? '');
  const [salesEmail, setSalesEmail] = useState(project.sales_executive_email ?? '');
  const [others, setOthers] = useState<StatusUpdateRecipient[]>(
    project.status_update_recipients ?? [],
  );

  useEffect(() => {
    if (!open) return;
    setPmUserId(project.pm_user_id);
    setPmName(project.pm_display_name ?? '');
    setPmEmail(project.pm_email ?? '');
    setIdn(project.interior_designer ?? '');
    setIdEmail(project.interior_designer_email ?? '');
    setCsm(project.csm ?? '');
    setCsmEmail(project.csm_email ?? '');
    setSales(project.sales_executive ?? '');
    setSalesEmail(project.sales_executive_email ?? '');
    setOthers(project.status_update_recipients ?? []);
  }, [open, project]);

  function save() {
    update.mutate(
      {
        pm_user_id: pmUserId,
        interior_designer: idn.trim() || null,
        interior_designer_email: idEmail.trim() || null,
        csm: csm.trim() || null,
        csm_email: csmEmail.trim() || null,
        sales_executive: sales.trim() || null,
        sales_executive_email: salesEmail.trim() || null,
        status_update_recipients: others.length ? others : null,
      },
      {
        onSuccess: () => {
          toast.success('People & contacts updated');
          onClose();
        },
        onError: (e) => toast.error(e.message || 'Could not save changes'),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit people & contacts"
      busy={update.isPending}
      className="max-w-lg"
    >
      <div className="p-4 space-y-4">
        <div>
          <Label>Requestor</Label>
          <RecipientAutocomplete
            value={pmName}
            onChange={setPmName}
            onPick={(r) => {
              if (!r.id) return; // must be a real Vari user, not a custom email
              setPmUserId(r.id);
              setPmName(r.display_name);
              setPmEmail(r.email);
            }}
            recipients={recipients}
            placeholder="Search staff to set the requestor…"
          />
          <p className="text-[11px] text-ink-faint mt-1">
            {pmUserId ? (
              <>
                Current: {pmName || '—'}
                {pmEmail ? ` · ${pmEmail}` : ''} ·{' '}
                <button
                  type="button"
                  className="text-accent hover:underline"
                  onClick={() => {
                    setPmUserId(null);
                    setPmName('');
                    setPmEmail('');
                  }}
                >
                  clear
                </button>
              </>
            ) : (
              'Pick a Vari staff member from the directory to set the requestor.'
            )}
          </p>
        </div>

        <div>
          <Label>Interior designer</Label>
          <RecipientAutocomplete
            value={idn}
            onChange={(n) => {
              setIdn(n);
              setIdEmail('');
            }}
            onPick={(r) => {
              setIdn(r.display_name);
              setIdEmail(r.email);
            }}
            recipients={recipients}
            allowCustomEmail
            placeholder="Name, or pick from the directory…"
          />
        </div>

        <div>
          <Label>CSM</Label>
          <RecipientAutocomplete
            value={csm}
            onChange={(n) => {
              setCsm(n);
              setCsmEmail('');
            }}
            onPick={(r) => {
              setCsm(r.display_name);
              setCsmEmail(r.email);
            }}
            recipients={recipients}
            allowCustomEmail
            placeholder="Name, or pick from the directory…"
          />
        </div>

        <div>
          <Label>Sales executive</Label>
          <RecipientAutocomplete
            value={sales}
            onChange={(n) => {
              setSales(n);
              setSalesEmail('');
            }}
            onPick={(r) => {
              setSales(r.display_name);
              setSalesEmail(r.email);
            }}
            recipients={recipients}
            allowCustomEmail
            placeholder="Name, or pick from the directory…"
          />
        </div>

        <div>
          <Label>Other contacts</Label>
          <p className="text-[11px] text-ink-faint mb-1.5">
            Anyone else who should get project + task status updates. Pick from the
            directory or type a custom email.
          </p>
          <OtherRecipientsEditor value={others} onChange={setOthers} recipients={recipients} />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
